import { NAV_STRUCTURE } from '../components/AppSidebar';

/**
 * Apparence des événements du Planning annuel — source unique côté navigateur.
 *
 * ── CE QUI CHANGE AU LOT 5.4 ter ──────────────────────────────────────────────
 *
 * LES CATÉGORIES NE SONT PLUS UNE CONSTANTE, CE SONT DES DONNÉES. Elles vivent
 * dans `evenements_categories` (migration v36) : par département, indépendantes de
 * l'année, renommables, recolorables, et porteuses du MODULE LIÉ. Toutes les
 * fonctions d'affichage prennent donc la liste chargée en second paramètre.
 *
 * Pourquoi ce déplacement : le lot 5.4 bis posait le module, le symbole et la
 * couleur sur CHAQUE ÉVÉNEMENT — 33 objets à classer, et à reclasser chaque
 * septembre puisque la liste des événements est recréée à chaque année. Les mêmes
 * 33 libellés ne produisent que ~10 familles, stables d'une année sur l'autre.
 *
 * ── LA CASCADE, DANS CET ORDRE ────────────────────────────────────────────────
 *
 *      image de l'événement  ->  dérogation de l'événement  ->  SA CATÉGORIE  ->  « Autre »
 *
 * L'événement gagne s'il porte quelque chose, et c'est voulu : un « Forum
 * entreprises » doit pouvoir afficher le logo de son partenaire. Mais c'est
 * l'EXCEPTION : la v36 a remis à NULL les couleurs et icônes que le serveur avait
 * écrites tout seul, sans quoi changer la couleur d'une catégorie n'aurait repeint
 * aucun événement et le référentiel aurait été purement décoratif.
 *
 * ⚠️ `CATEGORIES_AMORCAGE` NE SERT PAS À AFFICHER. C'est le miroir de
 * `backend/config/evenementsCategories.js`, employé uniquement comme repli quand la
 * liste n'est pas (encore) chargée. S'en servir pour l'affichage montrerait
 * « Réunion » à qui a renommé sa catégorie.
 */

/**
 * Les huit codes historiques, tels que le serveur les écrit à un département qui
 * n'a encore aucune catégorie. Libellés et couleurs repris de la maquette
 * redessinée le 2026-08-09, identiques à la migration v36 et au fichier serveur.
 */
export const CATEGORIES_AMORCAGE = [
    { code: 'soutenance', libelle: 'Soutenance',         icone: 'fa-microphone-lines', couleur: '#F97316', module_cle: 'soutenance' },
    { code: 'jury',       libelle: 'Jury',               icone: 'fa-scale-balanced',   couleur: '#EF4444', module_cle: 'jury' },
    { code: 'stage',      libelle: 'Stage & alternance', icone: 'fa-building',         couleur: '#16a34a', module_cle: 'stage' },
    { code: 'rentree',    libelle: 'Rentrée / Fin',      icone: 'fa-flag',             couleur: '#10B981', module_cle: null },
    { code: 'evenement',  libelle: 'Vie étudiante',      icone: 'fa-star',             couleur: '#EC4899', module_cle: null },
    { code: 'examen',     libelle: 'Examen',             icone: 'fa-pen',              couleur: '#8B5CF6', module_cle: null },
    { code: 'reunion',    libelle: 'Réunion',            icone: 'fa-users',            couleur: '#6B7280', module_cle: 'enseignants' },
    { code: 'autre',      libelle: 'Autre',              icone: 'fa-thumbtack',        couleur: '#1E293B', module_cle: null }
];

/**
 * Le filet. « Autre » n'est pas une catégorie comme les autres : c'est là que
 * retombent les événements d'une catégorie supprimée, et c'est pour cela qu'elle
 * n'a pas de poubelle. Un filet supprimable n'est pas un filet.
 */
export const CODE_FILET = 'autre';

/** Apparence de dernier recours — pour qu'aucune fonction ne renvoie `undefined`. */
const FILET = { code: CODE_FILET, libelle: 'Autre', icone: 'fa-thumbtack', couleur: '#1E293B', module_cle: null };

/**
 * Modules auxquels une catégorie peut être rattachée — construits depuis
 * NAV_STRUCTURE, la source de vérité de la charte. Rien n'est retapé : le libellé,
 * l'icône et la couleur affichés sont exactement ceux de la sidebar. Ajouter un
 * module à l'application l'ajoute ici sans y toucher.
 *
 * `admin` est écarté : il n'est pas dans la matrice des droits (son accès dépend du
 * rôle, pas d'un module), et rattacher un événement au panneau d'administration
 * n'aurait pas de sens.
 */
export const MODULES_EVENEMENTS = NAV_STRUCTURE
    .flatMap(section => section.items)
    .filter(item => item.key !== 'admin')
    .map(item => ({
        cle: item.key,
        label: item.label,
        icone: item.icon,
        couleur: item.topbarColor
    }));

/**
 * BIBLIOTHÈQUE D'ICÔNES DU RÉFÉRENTIEL — 33 glyphes, tous DÉJÀ utilisés ailleurs
 * dans l'applicatif.
 *
 * ⚠️ RÈGLE À TENIR : on n'ajoute ici que des icônes déjà présentes dans
 * l'application. La première version de cette liste proposait `fa-champagne-glasses`,
 * `fa-handshake`, `fa-trophy`… : un vocabulaire visuel neuf, inventé pour un seul
 * écran, exactement ce que la règle 6 interdit. Vérification faite sur les 138
 * glyphes réellement employés dans `frontend/src` avant d'établir cette liste.
 *
 * Deux familles, parce qu'elles répondent à deux questions différentes :
 *   « de quel module cette famille d'événements relève-t-elle ? » -> famille Modules
 *   « quel repère de calendrier est-ce ? »                        -> famille Calendrier
 *
 * Les huit icônes d'amorçage sont TOUTES dans cette liste : aucune catégorie
 * existante ne porte un glyphe qu'on ne pourrait pas retrouver.
 */
export const ICONES_EVENEMENTS = [
    {
        famille: 'Modules de l\'application',
        // Même ordre que la sidebar. Les libellés servent d'infobulle.
        icones: MODULES_EVENEMENTS.map(m => ({ classe: m.icone, label: m.label }))
    },
    {
        famille: 'Repères de calendrier',
        icones: [
            { classe: 'fa-flag',            label: 'Rentrée / fin' },
            { classe: 'fa-pen',             label: 'Examen' },
            { classe: 'fa-scale-balanced',  label: 'Jury' },
            { classe: 'fa-users',           label: 'Réunion' },
            { classe: 'fa-star',            label: 'Temps fort' },
            { classe: 'fa-thumbtack',       label: 'Repère' },
            { classe: 'fa-calendar-day',    label: 'Journée' },
            { classe: 'fa-calendar-week',   label: 'Semaine' },
            { classe: 'fa-calendar-check',  label: 'Échéance' },
            { classe: 'fa-clock',           label: 'Créneau' },
            { classe: 'fa-scroll',          label: 'Document' },
            { classe: 'fa-signature',       label: 'Signature' },
            { classe: 'fa-list-check',      label: 'Liste de contrôle' },
            { classe: 'fa-location-dot',    label: 'Lieu' },
            { classe: 'fa-lightbulb',       label: 'Information' }
        ]
    }
];

/** Toutes les classes proposées, à plat — sert au contrôle de validité. */
export const CLASSES_ICONES = ICONES_EVENEMENTS.flatMap(f => f.icones.map(i => i.classe));

/** Module par sa clé, ou `null`. */
export function moduleEvenement(cle) {
    return MODULES_EVENEMENTS.find(m => m.cle === cle) || null;
}

/**
 * La catégorie d'un code — jamais `undefined`.
 *
 * On cherche d'abord dans la liste CHARGÉE (la vérité du département), puis dans
 * l'amorçage (l'écran s'affiche avant la fin de la requête), puis « Autre ». Ce
 * dernier repli est ce qui rend la suppression d'une catégorie inoffensive : un
 * événement dont le code n'existe plus s'affiche en « Autre » au lieu de disparaître.
 */
export function categorieDe(code, categories) {
    if (Array.isArray(categories) && categories.length) {
        return categories.find(c => c.code === code)
            || categories.find(c => c.code === CODE_FILET)
            || FILET;
    }
    return CATEGORIES_AMORCAGE.find(c => c.code === code) || FILET;
}

/** Le nom affichable d'une catégorie. */
export function libelleCategorie(code, categories) {
    return categorieDe(code, categories).libelle;
}

/**
 * Classe FontAwesome à afficher pour un événement.
 * Sa dérogation d'abord, sa catégorie ensuite.
 *
 * Le test `startsWith('fa-')` n'est pas une précaution de style : une base qui
 * n'aurait pas reçu la v35 contiendrait des emoji dans cette colonne, et les
 * afficher violerait la charte. Le repli les ignore proprement.
 */
export function iconeEvenement(evenement, categories) {
    const stockee = evenement?.icone;
    if (typeof stockee === 'string' && stockee.startsWith('fa-')) return stockee;
    return categorieDe(evenement?.categorie, categories).icone;
}

/** Couleur à afficher : la dérogation de l'événement, sinon celle de sa catégorie. */
export function couleurEvenement(evenement, categories) {
    const stockee = evenement?.couleur;
    if (typeof stockee === 'string' && /^#[0-9A-Fa-f]{6}$/.test(stockee)) return stockee;
    return categorieDe(evenement?.categorie, categories).couleur;
}

/**
 * L'événement porte-t-il une apparence PROPRE (une dérogation) ?
 * Sert à l'annoncer plutôt qu'à la subir : un événement qui ne suit pas sa
 * catégorie doit pouvoir se repérer, sinon un changement de couleur de catégorie
 * paraît sans effet sur celui-là précisément.
 */
export function aUneDerogation(evenement) {
    return !!(evenement?.logo_url || evenement?.couleur || evenement?.icone);
}
